/**
 * Expense entry form — side sheet
 */
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useCreateExpense, useExpenseCategories, type CreateExpenseInput, type ExpenseSource } from '@/hooks/useExpenses';
import { Loader2, Save, ImagePlus } from 'lucide-react';

interface ExpenseFormProps {
  open: boolean;
  onClose: () => void;
}

const SOURCES: { value: ExpenseSource; label: string }[] = [
  { value: 'manual',         label: 'Manual' },
  { value: 'company_cash',   label: 'Company cash' },
  { value: 'sifi_card',      label: 'SIFI card' },
  { value: 'other_card',     label: 'Other card' },
  { value: 'driver_claim',   label: 'Driver claim' },
  { value: 'bank_transfer',  label: 'Bank transfer' },
  { value: 'petroapp',       label: 'PetroApp' },
];

export function ExpenseForm({ open, onClose }: ExpenseFormProps) {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState<CreateExpenseInput>({
    expenseDate: today,
    amountSar: 0,
    vatAmountSar: 0,
    source: 'manual',
    categoryId: null,
    vendorNameRaw: '',
    description: '',
    invoiceNumber: '',
    notes: '',
  });
  const [vatAuto, setVatAuto] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cats = useExpenseCategories();
  const create = useCreateExpense();

  // Auto-calculate VAT (15% of amount when category is VAT-eligible)
  const computedVat = useMemo(() => {
    if (!vatAuto) return form.vatAmountSar;
    const cat = cats.data?.find(c => c.id === form.categoryId);
    if (!cat?.is_vat_eligible) return 0;
    const amt = Number(form.amountSar) || 0;
    return Math.round(amt * 0.15 * 100) / 100;
  }, [vatAuto, form.amountSar, form.categoryId, cats.data, form.vatAmountSar]);

  const handleSubmit = async () => {
    setError(null);
    if (!form.amountSar || Number(form.amountSar) <= 0) {
      setError(t('expense.error.amount_required', 'Amount must be greater than 0'));
      return;
    }
    if (!form.categoryId) {
      setError(t('expense.error.category_required', 'Please select a category'));
      return;
    }
    try {
      await create.mutateAsync({ ...form, vatAmountSar: computedVat });
      // Reset & close
      setForm({
        expenseDate: today, amountSar: 0, vatAmountSar: 0, source: 'manual',
        categoryId: null, vendorNameRaw: '', description: '', invoiceNumber: '', notes: '',
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('expense.error.create_failed', 'Create failed'));
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('expense.new_title', 'New expense')}</SheetTitle>
          <SheetDescription>
            {t('expense.new_desc', 'Photo upload available after expense is saved.')}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-4">
          {/* Date + Source row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="exp-date">{t('expense.date', 'Date')}</Label>
              <Input
                id="exp-date"
                type="date"
                value={form.expenseDate}
                onChange={(e) => setForm({ ...form, expenseDate: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="exp-source">{t('expense.source', 'Payment method')}</Label>
              <select
                id="exp-source"
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value as ExpenseSource })}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          {/* Category */}
          <div>
            <Label htmlFor="exp-cat">{t('expense.category', 'Category')} *</Label>
            <select
              id="exp-cat"
              value={form.categoryId ?? ''}
              onChange={(e) => setForm({ ...form, categoryId: e.target.value || null })}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">— {t('expense.select_category', 'select category')} —</option>
              {cats.data?.map(c => (
                <option key={c.id} value={c.id}>{c.code} · {c.name_en}</option>
              ))}
            </select>
          </div>

          {/* Amount + VAT */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="exp-amt">{t('expense.amount_sar', 'Amount (SAR)')} *</Label>
              <Input
                id="exp-amt"
                type="number"
                step="0.01"
                min="0"
                value={form.amountSar || ''}
                onChange={(e) => setForm({ ...form, amountSar: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="exp-vat">{t('expense.vat_sar', 'VAT (SAR)')}</Label>
              <div className="flex gap-2">
                <Input
                  id="exp-vat"
                  type="number"
                  step="0.01"
                  min="0"
                  value={vatAuto ? computedVat : (form.vatAmountSar ?? 0)}
                  disabled={vatAuto}
                  onChange={(e) => setForm({ ...form, vatAmountSar: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <label className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                <input type="checkbox" checked={vatAuto} onChange={(e) => setVatAuto(e.target.checked)} />
                {t('expense.vat_auto', 'Auto-calculate (15%)')}
              </label>
            </div>
          </div>

          {/* Vendor */}
          <div>
            <Label htmlFor="exp-vendor">{t('expense.vendor', 'Vendor / supplier')}</Label>
            <Input
              id="exp-vendor"
              type="text"
              value={form.vendorNameRaw ?? ''}
              onChange={(e) => setForm({ ...form, vendorNameRaw: e.target.value })}
              placeholder="e.g., Saudi Aramco, Bridgestone, etc."
            />
          </div>

          {/* Invoice number */}
          <div>
            <Label htmlFor="exp-inv">{t('expense.invoice_number', 'Invoice number')}</Label>
            <Input
              id="exp-inv"
              type="text"
              value={form.invoiceNumber ?? ''}
              onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })}
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="exp-desc">{t('expense.description', 'Description')}</Label>
            <textarea
              id="exp-desc"
              rows={2}
              value={form.description ?? ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="exp-notes">{t('expense.notes', 'Internal notes')}</Label>
            <textarea
              id="exp-notes"
              rows={2}
              value={form.notes ?? ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Photo upload notice */}
          <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 flex items-start gap-2">
            <ImagePlus className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{t('expense.photo_after', 'After saving, you can attach the receipt photo from the expense detail.')}</span>
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}
        </div>

        <div className="flex gap-2 justify-end border-t border-border pt-4 sticky bottom-0 bg-background">
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={create.isPending} variant="gold">
            {create.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {t('expense.save', 'Save expense')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
